import { useState, useEffect } from 'react';
import { getMonitorStatus } from '../utils/api';

interface MonitorStatusIndicatorProps {
  className?: string;
  onMonitorComplete?: () => void; // 新增：监控完成回调
}

/**
 * 监控状态指示器组件
 * 显示当前监控状态和距离下次执行的时间进度
 */
const MonitorStatusIndicator: React.FC<MonitorStatusIndicatorProps> = ({ 
  className = '', 
  onMonitorComplete 
}) => {
  const [status, setStatus] = useState<{
    isMonitoring: boolean;
    intervalMinutes: number;
    nextRunTime?: string;
    timeUntilNext?: number;
  } | null>(null);
  const [progress, setProgress] = useState(0);
  const [timeDisplay, setTimeDisplay] = useState('');
  const [loading, setLoading] = useState(true);
  const [previousTimeUntilNext, setPreviousTimeUntilNext] = useState<number | null>(null);

  // 加载监控状态
  const loadStatus = async () => {
    try {
      const statusData = await getMonitorStatus();
      
      // 检测监控任务是否刚刚完成
      // 如果前一次的剩余时间很短（小于10秒），现在又重新开始了一个新的周期，说明监控任务刚刚执行完成
      if (previousTimeUntilNext !== null && 
          previousTimeUntilNext < 10000 && // 前一次剩余时间小于10秒
          statusData.timeUntilNext && 
          statusData.timeUntilNext > previousTimeUntilNext * 2) { // 新的剩余时间大于前一次的2倍，说明是新周期
        
        console.log('[MonitorStatusIndicator] 检测到监控任务完成，通知刷新产品列表');
        onMonitorComplete?.();
      }
      
      // 更新前一次的剩余时间
      setPreviousTimeUntilNext(statusData.timeUntilNext || null);
      
      setStatus(statusData);
    } catch (error) {
      console.error('获取监控状态失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 格式化剩余时间显示
  const formatTimeRemaining = (milliseconds: number) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}时${minutes}分${seconds}秒`;
    } else if (minutes > 0) {
      return `${minutes}分${seconds}秒`;
    } else {
      return `${seconds}秒`;
    }
  };

  // 计算进度百分比
  const calculateProgress = (timeUntilNext: number, intervalMinutes: number) => {
    const totalTime = intervalMinutes * 60 * 1000; // 总时间（毫秒）
    const elapsed = totalTime - timeUntilNext; // 已经过去的时间
    return Math.max(0, Math.min(100, (elapsed / totalTime) * 100));
  };

  // 更新状态和进度
  useEffect(() => {
    loadStatus();
    
    // 每5秒刷新一次状态
    const statusInterval = setInterval(loadStatus, 5000);
    
    return () => clearInterval(statusInterval);
  }, []);

  // 实时更新倒计时和进度条
  useEffect(() => {
    if (!status?.isMonitoring || !status?.timeUntilNext) {
      return;
    }

    const updateTimer = setInterval(() => {
      setStatus(prev => {
        if (!prev?.timeUntilNext) return prev;
        
        const newTimeUntilNext = Math.max(0, prev.timeUntilNext - 1000);
        
        if (newTimeUntilNext === 0) {
          // 时间到了，监控任务应该开始执行
          console.log('[MonitorStatusIndicator] 监控时间到，开始执行监控任务');
          
          // 延迟几秒后重新加载状态并通知刷新
          setTimeout(() => {
            console.log('[MonitorStatusIndicator] 监控任务执行完成，通知刷新产品列表');
            onMonitorComplete?.();
            loadStatus();
          }, 3000); // 给监控任务3秒的执行时间
          
          return prev;
        }
        
        return {
          ...prev,
          timeUntilNext: newTimeUntilNext
        };
      });
    }, 1000);

    return () => clearInterval(updateTimer);
  }, [status?.isMonitoring, status?.timeUntilNext]);

  // 更新进度和时间显示
  useEffect(() => {
    if (status?.timeUntilNext && status?.intervalMinutes) {
      const progressValue = calculateProgress(status.timeUntilNext, status.intervalMinutes);
      setProgress(progressValue);
      setTimeDisplay(formatTimeRemaining(status.timeUntilNext));
    }
  }, [status?.timeUntilNext, status?.intervalMinutes]);

  if (loading) {
    return (
      <div className={`stat bg-gradient-to-r from-blue-500/10 to-blue-600/10 rounded-lg p-3 ${className}`}>
        <div className="stat-title text-xs">自动监控状态</div>
        <div className="flex items-center space-x-2">
          <span className="loading loading-spinner loading-xs"></span>
          <span className="text-sm">加载中...</span>
        </div>
      </div>
    );
  }

  if (!status?.isMonitoring) {
    return (
      <div className={`stat bg-gradient-to-r from-gray-500/10 to-gray-600/10 rounded-lg p-3 ${className}`}>
        <div className="stat-title text-xs">自动监控状态</div>
        <div className="flex items-center space-x-2">
          <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
          <span className="text-sm text-gray-600">监控已停止</span>
        </div>
        <div className="stat-desc text-xs mt-1">请开启自动监控</div>
      </div>
    );
  }

  return (
    <div className={`stat bg-gradient-to-r from-green-500/10 to-green-600/10 rounded-lg p-3 ${className}`}>
      <div className="stat-title text-xs flex items-center space-x-2">
        <span>自动监控状态</span>
        <div className="flex items-center space-x-1">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          <span className="text-green-600 text-xs font-medium">监控中</span>
        </div>
      </div>
      
      <div className="mt-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-gray-600">距离下次执行</span>
          <span className="text-xs font-medium text-green-600">{timeDisplay}</span>
        </div>
        
        {/* 进度条 */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-1000 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        
        <div className="stat-desc text-xs mt-1">
          每{status.intervalMinutes}分钟自动监控
        </div>
      </div>
    </div>
  );
};

export default MonitorStatusIndicator; 